import { t } from "i18next";

/**
 * Login hata yanıtının UI'a yansıması.
 *
 * Pentest 3.1.1 (kullanıcı adı tespiti): 401 yanıtlarında backend mesajı ASLA
 * gösterilmez — yanlış şifre, olmayan kullanıcı, pasif hesap ve yetkisiz portal
 * tek bir genel metne ("Kullanıcı adı veya şifre hatalı.") düşer. Yalnızca
 * 403 (çok fazla deneme), 429 (rate limit), captcha ve ağ hataları kendi
 * metinlerini gösterir.
 */
export interface LoginErrorResult {
  /** Bildirimde gösterilecek, çevrilmiş metin */
  message: string;
  /** Kullanıcı adı / şifre alanları kırmızı işaretlensin mi */
  markFields: boolean;
  /**
   * Backend'in captcha isteyip istemediği. `undefined` → yanıt bu konuda bilgi
   * vermedi (ör. ağ hatası); mevcut durum korunmalı.
   */
  captchaRequired?: boolean;
}

export const CAPTCHA_ERROR_CODES = [
  "CAPTCHA_REQUIRED",
  "CAPTCHA_INVALID",
] as const;

interface LoginErrorBody {
  errorCode?: unknown;
  captchaRequired?: unknown;
}

interface LoginErrorLike {
  code?: string;
  response?: { status?: number; data?: unknown };
}

const readBody = (data: unknown): LoginErrorBody =>
  data && typeof data === "object" ? (data as LoginErrorBody) : {};

export const resolveLoginError = (error: unknown): LoginErrorResult => {
  const err = (error ?? {}) as LoginErrorLike;
  const response = err.response;
  if (!response || err.code === "ERR_NETWORK") {
    return { message: t("staffLogin.serverUnreachable"), markFields: false };
  }

  const status = response.status;
  const body = readBody(response.data);
  const errorCode =
    typeof body.errorCode === "string" ? body.errorCode : undefined;
  const isCaptchaError =
    errorCode !== undefined &&
    (CAPTCHA_ERROR_CODES as readonly string[]).includes(errorCode);
  let captchaRequired: boolean | undefined =
    typeof body.captchaRequired === "boolean"
      ? body.captchaRequired
      : undefined;
  if (isCaptchaError) captchaRequired = true;

  if (isCaptchaError) {
    return {
      message:
        errorCode === "CAPTCHA_INVALID"
          ? t("login.captcha.invalid")
          : t("login.captcha.required"),
      markFields: false,
      captchaRequired,
    };
  }

  if (status === 401) {
    return {
      message: t("login.invalidCredentials"),
      markFields: true,
      captchaRequired,
    };
  }

  if (status === 403) {
    // Login uçlarında 403 yalnızca TOO_MANY_LOGIN_ATTEMPTS içindir.
    return {
      message: t("login.tooManyAttempts"),
      markFields: false,
      captchaRequired,
    };
  }

  if (status === 429) {
    return {
      message: t("login.tooManyRequests"),
      markFields: false,
      captchaRequired,
    };
  }

  return {
    message: t("login.loginFailed"),
    markFields: false,
    captchaRequired,
  };
};
